import { createPortal } from 'react-dom';

/**
 * StepUpModal — heads-up prompt shown before a step-up re-auth redirect
 * (Tier 3 session hardening). A critical action (delete/export) past the
 * step-up freshness window returns `step_up_required`; rather than yanking the
 * user to the Portal with no warning, we explain what's about to happen and let
 * them choose. Continue → redirect to the Portal for a fresh password+MFA login
 * (then bounce back here). Cancel → stay put; the action is abandoned.
 *
 * Portaled to <body> so a transformed ancestor can't clip the fixed overlay.
 *
 * `google` (google_identity provider, AW plan P1.11): the re-auth is a
 * browser-side Google logout chained into the authorize URL — the only
 * thing that makes Google ask for a factor again — so say plainly that
 * this browser gets signed out of Google (every account in the profile).
 */
export default function StepUpModal({ action, onContinue, onCancel, google = false }) {
  const what = action?.description || 'This action';
  return createPortal((
    <div className="np-export-overlay" onClick={onCancel}>
      <div className="np-export-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <div className="np-export-head">
          <h3>Re-verify your identity</h3>
          <button className="np-icon-btn" onClick={onCancel} title="Cancel">✕</button>
        </div>
        <div className="np-export-body" style={{ display: 'block', padding: 16 }}>
          <p style={{ marginTop: 0, fontSize: 13, color: '#555' }}>
            For your security, <b>{what.toLowerCase()}</b> requires a fresh sign-in.
          </p>
          <p style={{ marginBottom: 0, fontSize: 13, color: '#555' }}>
            {google ? (
              <>
                You’ll be signed out of Google in this browser and asked to sign in
                again with your passkey or authenticator app, then brought right back
                to where you left off. Any other Google account open in this browser
                will be signed out too.
              </>
            ) : (
              <>
                You’ll be sent to the ChargeStack sign-in page to verify your identity,
                then brought right back to where you left off.
              </>
            )}
          </p>
          {action?.error && (
            <p role="alert" style={{ marginTop: 10, marginBottom: 0, fontSize: 13, color: '#b91c1c' }}>
              {action.error}
            </p>
          )}
        </div>
        <div className="np-export-foot">
          <span style={{ flex: 1 }} />
          <button className="btn" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" onClick={onContinue} autoFocus>
            Continue to sign in
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}
